
/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import { debounceTime } from 'rxjs/operators';
import { LocoDetail } from '../../../core/model/loco-detail.model';
import { MenuItem } from '../../../core/model/menu-item.model';
import { TabSearchParameter } from '../../../core/model/tab-search-parameter.model';
import { AppUtilityService } from '../../../core/services/app-utility.service';
import { ConfigService } from '../../../core/services/config.service';
import { LmsMenuService } from '../../../core/services/lms-menu.service';
import { LmsRendererService } from '../../../core/services/lms-renderer.service';
import { MessageHandlerService } from '../../../core/services/message-handler.service';
import { StaticDataStoreService } from '../../../core/services/static-datastore.service';
import { TabService } from '../../../core/services/tab.service';
import { Alert } from '../../shared/model/alert.model';


@Component({
  selector: 'lms-alert-details',
  templateUrl: './alert-details.component.html',
  styleUrls: ['./alert-details.component.scss']
})
export class AlertDetailsComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() alerts: Array<Alert>;
  @Input() alertLevel: string;
  @Input() lastAccessedTime: string;
  @Input() signOffStatus: boolean;
  @Input() dirSignOffAlerts: Array<Alert>;
  @Input() deskSignOffAlerts: Array<Alert>;
  showFrom: number;
  showTo: number;
  totalRecords: number;
  scrollHeight: string;
  configData: any;
  allAlerts: Array<Alert>;
  redBackground: boolean;
  zoneList: any;
  locoLinks: Array<MenuItem>;
  trainLinks: Array<MenuItem>;
  overlayLinks: Array<MenuItem>;
  overlayPanel: OverlayPanel;
  panelVisible = false;
  contextType: string;
  contextSearchText: string;
  currentContextLoco: Alert;
  readonly upperHeight = 228;

  @Output() deskSignedEvent: EventEmitter<Array<Alert>> = new EventEmitter<Array<Alert>>();
  @Output() dirSignedEvent: EventEmitter<Array<Alert>> = new EventEmitter<Array<Alert>>();
  @Output() showLocomotiveContextMenu: EventEmitter<LocoDetail> = new EventEmitter<LocoDetail>();

  /**
   * Creates an instance of AlertDetailsComponent.
   * @param {ConfigService} configService
   * @param {AppUtilityService} appUtilityService
   * @param {Router} router
   * @param {StaticDataStoreService} staticDataStoreService
   * @memberof AlertDetailsComponent
   */
  constructor(private configService: ConfigService, private appUtilityService: AppUtilityService,
    private lmsMenuService: LmsMenuService, private lmsRendererService: LmsRendererService,) {
    this.configData = this.configService.getConfigData();
    this.setTableScrollHeight();
    this.allAlerts = [];
    this.redBackground = false;
    this.locoLinks = this.lmsMenuService.getMenuItem('Locomotive').subTypes;
    this.trainLinks = this.lmsMenuService.getMenuItem('Train').subTypes;
    this.lmsRendererService.resizeObervable.pipe(debounceTime(10)).subscribe((isResize) => {
      if (isResize) {
        this.setTableScrollHeight();
        setTimeout(() => {
          this.scrollEventOccured();
        }, 5);
      }
    });
  }

  private setTableScrollHeight() {
    const windowHeight = this.lmsRendererService.getWindowHeight();
    this.scrollHeight = (windowHeight - this.upperHeight) + 'px';
  }

  /**
   * this method is used when data is rendered in DOM
   * for fetched alerts. this method corrects the alignment
   * of alerts table when there is no scrollbar in table for data
   * @memberof AlertDetailsComponent
   */
  ngAfterViewInit() {
    const alertsTable = document.getElementById('alertsDetailsWindow');
    if (alertsTable && alertsTable.children[0] && alertsTable.children[0].children[0] && alertsTable.children[0].children[0].children[0]
      && alertsTable.children[0].children[0].children[0].children[0]) {
      const scrollEvent = this.scrollEventOccured.bind(this);
      const tableHeader = alertsTable.children[0].children[0].children[0].children[0].children[0];
      const headerComputed = tableHeader.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const calcScrollHeight = (windowHeight - (headerComputed.top + headerComputed.height));
      const scrollableBody = alertsTable.children[0].children[0].children[0].children[1];
      scrollableBody.addEventListener('scroll', scrollEvent);
      if (!this.alerts || Math.ceil((calcScrollHeight / 46)) > this.alerts.length) {
        tableHeader.removeAttribute('style');
      }
      this.scrollEventOccured();
    }
  }

  public scrollEventOccured() {
    const alertsTable = document.getElementById('alertsDetailsWindow');
    if (alertsTable && alertsTable.children[0] && alertsTable.children[0].children[0] &&
      alertsTable.children[0].children[0].children[0] && alertsTable.children[0].children[0].children[0].children[1]) {
      const scrollableBody = alertsTable.children[0].children[0].children[0].children[1];
      const clientHeight = scrollableBody.clientHeight;
      const scrollHeight = scrollableBody.scrollHeight;
      const scrollTop = scrollableBody.scrollTop;

      this.totalRecords = this.allAlerts.length;
      const uprHiddenRown = Math.floor(scrollTop / 46);
      const displayedRows = Math.floor(clientHeight / 46);
      const lowerHiddenRows = this.totalRecords - uprHiddenRown - displayedRows;
      this.showFrom = uprHiddenRown + 1;
      this.showTo = this.showFrom + displayedRows;
      if (this.showFrom >= this.allAlerts.length) {
        this.showFrom = this.allAlerts.length - displayedRows;
      }
      if (this.showFrom < 1 && this.allAlerts && this.allAlerts.length > 0) {
        this.showFrom = 1;
      }
      if (this.showTo > this.allAlerts.length) {
        this.showTo = this.allAlerts.length;
      }
      if (this.allAlerts && this.allAlerts.length === 0) {
        this.showFrom = 0;
        this.showTo = 0;
      }
    }
  }

  /**
   * this method is used when user checks the desk signOff
   * checkbox for any alert. this method also emitts a event
   * to inform parent component
   * @param {Alert} alert
   * @param {any} event
   * @memberof AlertDetailsComponent
   */
  singOffActionDesk(alert: Alert, event) {
    if (event?.checked) {
      this.deskSignOffAlerts.push(alert);
    } else {
      this.deskSignOffAlerts = this.deskSignOffAlerts.filter((newAlert) => {
        return newAlert.alertSeq !== alert.alertSeq;
      });
    }
    this.deskSignedEvent.emit(this.deskSignOffAlerts);
  }

  /**
   * this method is used when user checks the director signOff
   * checkbox for any alert. this method also emitts a event
   * to inform parent component
   * @param {Alert} alert
   * @param {any} event
   * @memberof AlertDetailsComponent
   */
  singOffActionDir(alert: Alert, event) {
    if (event) {
      this.dirSignOffAlerts.push(alert);
    } else {
      this.dirSignOffAlerts = this.dirSignOffAlerts.filter((newAlert) => {
        return newAlert.alertSeq !== alert.alertSeq;
      });
    }
    this.dirSignedEvent.emit(this.dirSignOffAlerts);
  }

  /**
   * this method is the lifecycle method
   * of this component and initializes the alerts
   * to empty array
   * @memberof AlertDetailsComponent
   */
  ngOnInit() {
    if (!this.allAlerts) {
      this.allAlerts = [];
    }
  }

  /**
   * this method check for certain things when any changes
   * are there in this component by parent component
   * @memberof AlertDetailsComponent
   */
  ngOnChanges() {
    if (this.alerts && this.alerts.length > 0 && !this.signOffStatus && this.deskSignOffAlerts && this.deskSignOffAlerts.length < 1 && this.dirSignOffAlerts && this.dirSignOffAlerts.length < 1) {
      this.allAlerts = this.alerts.filter((alert) => {
        if (alert.priority === '1') {
          if (alert.deskCheckStatus && alert.dirCheckStatus) {
            return false;
          } else {
            return true;
          }
        }
        if (alert.priority !== '1') {
          if (alert.deskCheckStatus) {
            return false;
          } else {
            return true;
          }
        }
      });
    } else if (!this.alerts || this.alerts.length < 1) {
      this.allAlerts = [];
    }
    if (this.alerts && this.alerts.length > 0 && this.signOffStatus) {
      this.allAlerts = this.alerts;
    }
    this.scrollEventOccured();
  }

  /**
   * this mehtod sort the gievn alerts array
   * on the basis of alerts priority
   * @param {Array<Alert>} alerts
   * @returns {Array<Alert>}
   * @memberof AlertDetailsComponent
   */
  sortAlertByPriority(alerts: Array<Alert>): Array<Alert> {
    if (alerts) {
      return alerts.sort((alert1, alert2) => {
        if (parseInt(alert1.priority, 10) < parseInt(alert2.priority, 10)) {
          return 1;
        } else if (parseInt(alert1.priority, 10) > parseInt(alert2.priority, 10)) {
          return -1;
        } else if (parseInt(alert1.priority, 10) === parseInt(alert2.priority, 10)) {
          return 0;
        }
      });
    } else {
      return [];
    }
  }

  /**
   * this method is used to check whether
   * a given alert is displayed in bold text or not
   * @param {Alert} alert
   * @returns
   * @memberof AlertDetailsComponent
   */
  public getBoldStatusOfAlert(alert: Alert) {
    const lastAccesedDate = new Date(this.lastAccessedTime);
    const createDate = new Date(alert.creationTs);
    if (createDate > lastAccesedDate) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * this mehtod is used to set the list of links
   * that will appear when user right clicks on given
   * loco/train link
   * @param {string} type
   * @memberof AlertDetailsComponent
   */
  setOverlayLinks(type: string) {
    this.checkForOverlayPanelVisibility();
    if (type === 'loco') {
      this.overlayLinks = this.locoLinks;
    } else if (type === 'train') {
      this.overlayLinks = this.trainLinks;
    }
  }

  /**
   * this mehtod is used to set the overlay position
   * when user right clicks on the train/loco link
   * @param {any} event
   * @param {OverlayPanel} panel
   * @param {Alert} alert
   * @param {string} obj
   * @returns
   * @memberof AlertDetailsComponent
   */
  public locomotiveOverlayPosition(event, panel: OverlayPanel, alert: Alert, obj: string) {
    this.overlayPanel = panel;
    if (!panel.overlayVisible && this.panelVisible === true) {
      this.panelVisible = false;
    }
    if (this.panelVisible) {
      panel.hide();
      this.panelVisible = false;
      event.stopPropagation();
      return;
    }
    const overlayPanel = document.getElementById('overlayLinksInfo').getElementsByClassName('p-overlaypanel');
    let element;
    if (obj === 'loco') {
      element = document.getElementById('locomotive' + alert.locoId).parentElement;
      this.contextType = 'Locomotive';
      this.contextSearchText = alert.locoId;
    } else if (obj === 'train') {
      element = document.getElementById('train' + alert.trainId).parentElement;
      this.contextType = 'Train';
      this.contextSearchText = alert.trainId;
    }
    panel.show(event);
    const rect = element.getBoundingClientRect();
    const scrollHeight = window.pageYOffset;
    const windowHeight = window.innerHeight;
    let overlayHeight = 0;
    if (this.overlayLinks && this.overlayLinks.length > 0) {
      overlayHeight = this.overlayLinks.length * 30;
    } else {
      panel.hide();
      return;
    }
    let top;
    if ((windowHeight - rect.top - rect.height) > overlayHeight) {
      top = rect.top + rect.height + scrollHeight + ((alert.background && alert.background === 'red') ? 33 : 0) + 'px';
    } else {
      top = (rect.top - overlayHeight) + scrollHeight + ((alert.background && alert.background === 'red') ? 33 : 0) + 'px';
    }
    const left = rect.left + ((alert.background && alert.background === 'red') ? 8 : 0) + 'px';
    const width = rect.width + 'px';
    let style = `display: block; z-index: 1001; visibility: visible; 
            opacity: 1.032; top: <<top>>; left: <<left>>; width: <<width>>;`;
    style = style.replace('<<top>>', top);
    style = style.replace('<<left>>', left);
    style = style.replace('<<width>>', width);
    overlayPanel[0].setAttribute('style', style);
    event.stopPropagation();
    this.panelVisible = true;
    this.currentContextLoco = alert;
  }

  /**
   * this mehtod is used to hide previously
   * opened overlay panel
   * @param {any} event
   * @param {OverlayPanel} panel
   * @returns
   * @memberof AlertDetailsComponent
   */
  public hideLocoOverlayPanel(event, panel: OverlayPanel) {
    this.checkForOverlayPanelVisibility();
    if (this.panelVisible) {
      panel.hide();
      this.panelVisible = false;
      panel = null;
      this.currentContextLoco = null;
      return;
    }
  }

  /**
   * this method is check whether any overlay
   * panel is opened on page or not. If yes, then
   * this methos will hide the opened panel.
   * @memberof AlertDetailsComponent
   */
  checkForOverlayPanelVisibility() {
    if (this.overlayPanel) {
      this.overlayPanel.hide();
      this.currentContextLoco = null;
      this.overlayPanel = null;
      this.contextType = null;
      this.contextSearchText = null;
      this.overlayLinks = null;
    }
  }

  /**
   * this method captures the click event
   * on the page to hide overlay panel
   * @param {any} event
   * @memberof AlertDetailsComponent
   */
  @HostListener('document:click') hideAddTabPanel(event) {
    this.checkForOverlayPanelVisibility();
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * this method is used to navigate to screen
   * based on the given inputs
   * @param {string} subType
   * @param {string} type
   * @param {string} searchText
   *
   * @memberof AlertDetailsComponent
   */
  public routeToPage(subType: string, type: string, searchText: string) {
    if (type === 'Locomotive') {
      searchText = this.appUtilityService.formatLocomotiveIdForDispaly(searchText);
    }
    const tabSearchParameter = <TabSearchParameter>({ searchText: searchText });
    this.appUtilityService.navigateByUrl(type, subType, searchText, true);
  }
}
